import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { getUserList } from '../../../../slices/user/thunk';
import { getInspectionTypeList } from '../../../../slices/inspectionType/thunk';
import { getInspectionAuthorityList } from '../../../../slices/inspectionAuthority/thunk';
import { getBreachCategoryList } from '../../../../slices/breachCategory/thunk';
import { getBreachOriginList } from '../../../../slices/breachOrigin/thunk';
import { getBreachImpactList } from '../../../../slices/breachImpact/thunk';
import { getBreachResponsibleAreaList } from '../../../../slices/breachResponsibleArea/thunk';
import { getBreachRiskClassificationList } from '../../../../slices/breachRiskClassification/thunk';
import { getBreachTypeOfEvidenceList } from '../../../../slices/breachTypeOfEvidence/thunk';
import { getBreachStatusList } from '../../../../slices/breachStatus/thunk';
import { getActivityStatusList } from '../../../../slices/activityStatus/thunk';
import { getAuthorityStatusList } from '../../../../slices/authorityStatus/thunk';
import {
	addNewBreachRecord,
	getBreachRecord,
	getBreachRecordList,
	updateBreachRecord,
} from '../../../../slices/breachRecord/thunk';

import { uploadFiles } from '../../../../helpers/file_manager/backend_helper';
import { toStringId } from '../Forms/utils';
import { BREACH_RECORD_POPULATE, BREACH_RECORD_FIELDS } from '../constants';

//Module-level helpers

const getUserDisplayName = user =>
	user?.full_name ||
	[user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
	user?.name ||
	user?.username ||
	'';

const normalizeBreachRecord = item => ({
	...item,
	_id: toStringId(item?._id),
	inspection_type_id: toStringId(item?.inspection_types?._id || item?.inspection_type?._id),
	breach_responsible_area_id: toStringId(item?.breach_responsible_area?._id),
	responsible_user_ids: Array.isArray(item?.responsible_user_ids)
		? item.responsible_user_ids.map(toStringId)
		: Array.isArray(item?.responsible_users) && item.responsible_users.length > 0
		? item.responsible_users.map(u => toStringId(u._id))
		: item?.responsible_user_id
		? [toStringId(item.responsible_user_id)]
		: [],
	breach_impact_id: toStringId(item?.breach_impact?._id),
	breach_type_of_evidence_id: toStringId(item?.breach_type_of_evidence?._id),
	breach_category_id: toStringId(item?.breach_category?._id),
	breach_origin_id: toStringId(item?.breach_origin?._id),
	breach_risk_classification_id: toStringId(item?.breach_risk_classification?._id),
	breach_status_id: toStringId(item?.breach_status?._id),
	authority_status_id: toStringId(item?.authority_status?._id),
	inspection_authority_ids: Array.isArray(item?.inspection_authority_ids)
		? item.inspection_authority_ids.map(toStringId)
		: Array.isArray(item?.inspection_authorities) && item.inspection_authorities.length > 0
		? item.inspection_authorities.map(a => toStringId(a._id))
		: item?.inspection_authority_id
		? [toStringId(item.inspection_authority_id)]
		: item?.inspection_authority?._id
		? [toStringId(item.inspection_authority._id)]
		: [],
	// Cached display names for table badges
	_impact_name: item?.breach_impact?.name || '',
	_category_name: item?.breach_category?.name || '',
	_risk_name: item?.breach_risk_classification?.name || '',
	_origin_name: item?.breach_origin?.name || '',
	finding_closure_file_id: item?.finding_closure_file_id || null,
});

const fetchBreachRecordList = (dispatch, inspectionId) =>
	dispatch(
		getBreachRecordList({
			filters: {
				disable: { $eq: false },
				inspections_id: { $eq: inspectionId },
			},
			populate: BREACH_RECORD_POPULATE,
			fields: BREACH_RECORD_FIELDS,
		})
	);

//Hook

const useIncumplimientosManagement = ({ dataFiscalizacion, isOpen, onDataChange }) => {
	const dispatch = useDispatch();

	const { user: profileUser } = useSelector(state => state.Profile);
	const { userList } = useSelector(state => state.Users);
	const { inspectionTypeListSelect } = useSelector(state => state.InspectionType);
	const { inspectionAuthorityListSelect } = useSelector(state => state.InspectionAuthority);
	const { breachCategoryListSelect } = useSelector(state => state.BreachCategory);
	const { breachOriginListSelect } = useSelector(state => state.BreachOrigin);
	const { breachImpactListSelect } = useSelector(state => state.BreachImpact);
	const { breachResponsibleAreaListSelect } = useSelector(state => state.BreachResponsibleArea);
	const { breachRiskClassificationListSelect } = useSelector(state => state.BreachRiskClassification);
	const { breachTypeOfEvidenceListSelect } = useSelector(state => state.BreachTypeOfEvidence);
	const { breachRecordList, isLoadingList, currentBreachRecord, isLoadingCurrent } = useSelector(
		state => state.BreachRecord
	);
	const { breachStatusListSelect } = useSelector(state => state.BreachStatus);
	const { activityStatusListSelect } = useSelector(state => state.ActivityStatus);
	const { authorityStatusListSelect } = useSelector(state => state.AuthorityStatus);

	const [dataIncumplimiento, setDataIncumplimiento] = useState({});
	const [modalAdd, setModalAdd] = useState(false);
	const [modalDelete, setModalDelete] = useState(false);
	const [isEdit, setIsEdit] = useState(false);
	const [pendingEditOpen, setPendingEditOpen] = useState(false);

	const inspectionId = toStringId(dataFiscalizacion?._id);
	const isEnRevision = !!dataFiscalizacion?.waiting_for_observations;

	//Fetch lookup tables when modal opens
	useEffect(() => {
		if (!isOpen) return;
		dispatch(getUserList());
		dispatch(getInspectionTypeList());
		dispatch(getInspectionAuthorityList());
		dispatch(getBreachCategoryList());
		dispatch(getBreachOriginList());
		dispatch(getBreachImpactList());
		dispatch(getBreachResponsibleAreaList());
		dispatch(getBreachRiskClassificationList());
		dispatch(getBreachTypeOfEvidenceList());
		dispatch(getBreachStatusList());
		dispatch(getActivityStatusList());
		dispatch(getAuthorityStatusList());
	}, [dispatch, isOpen]);

	//Fetch breach records for this inspection
	useEffect(() => {
		if (!isOpen || !inspectionId) return;
		fetchBreachRecordList(dispatch, inspectionId);
	}, [dispatch, isOpen, inspectionId]);

	//Open edit modal once the full record is loaded
	useEffect(() => {
		if (pendingEditOpen && currentBreachRecord && !isLoadingCurrent) {
			setDataIncumplimiento(normalizeBreachRecord(currentBreachRecord));
			setModalAdd(true);
			setPendingEditOpen(false);
		}
	}, [currentBreachRecord, isLoadingCurrent, pendingEditOpen]);

	//Derived data
	const responsibleOptions = useMemo(
		() =>
			(userList || []).map(item => ({
				item,
				value: toStringId(item._id),
				label: getUserDisplayName(item),
			})),
		[userList]
	);

	const pendienteId = useMemo(
		() =>
			breachStatusListSelect?.find(item => item.label?.toLowerCase() === 'pendiente')?.value || '',
		[breachStatusListSelect]
	);

	const pendienteActivityStatusId = useMemo(
		() =>
			activityStatusListSelect?.find(item => item.label?.toLowerCase() === 'pendiente')?.value || '',
		[activityStatusListSelect]
	);

	const pendienteAuthorityStatusId = useMemo(
		() =>
			authorityStatusListSelect?.find(item => item.label?.toLowerCase() === 'pendiente')?.value || '',
		[authorityStatusListSelect]
	);

	const incumplimientos = useMemo(() => {
		const source = Array.isArray(breachRecordList) ? breachRecordList : [];
		return source
			.filter(item => toStringId(item?.inspections_id) === inspectionId)
			.map(normalizeBreachRecord);
	}, [breachRecordList, inspectionId]);

	//CRUD handlers
	const handleClickNew = () => {
		setDataIncumplimiento({});
		setIsEdit(false);
		setModalAdd(true);
	};

	const handleClickEdit = incumplimiento => {
		setIsEdit(true);
		setPendingEditOpen(true);
		dispatch(getBreachRecord(toStringId(incumplimiento._id)));
	};

	const handleClickDelete = incumplimiento => {
		setDataIncumplimiento(incumplimiento);
		setModalDelete(true);
	};

	const handleSaveIncumplimiento = async values => {
		if (!inspectionId) {
			toast.error('No se encontro la fiscalizacion asociada.');
			return;
		}

		const actor =
			profileUser?.username ||
			profileUser?.email ||
			profileUser?._id ||
			profileUser?.id ||
			'system';

		const { finding_closure_document, finding_closure_file_id: existingClosureFileId, ...formValues } = values;
		let closureFileId = null;
		if (finding_closure_document instanceof File) {
			const formData = new FormData();
			formData.append('files', finding_closure_document);
			const uploadResponse = await uploadFiles({ formData });
			if (!uploadResponse || !uploadResponse.length || !uploadResponse[0]._id) {
				toast.error('Error al subir el respaldo de cierre de hallazgo');
				return;
			}
			closureFileId = uploadResponse[0]._id;
		} else if (existingClosureFileId) {
			closureFileId = existingClosureFileId;
		}

		const payload = {
			...formValues,
			finding_closure_file_id: closureFileId,
			inspections_id: inspectionId,
			inspection_type_id:
				values.inspection_type_id || toStringId(dataFiscalizacion?.tipo),
			responsible_user_ids: Array.isArray(values.responsible_user_ids)
				? values.responsible_user_ids
				: [],
			breach_status_id: isEdit
				? dataIncumplimiento.breach_status_id || ''
				: pendienteId,
			activity_status_id: isEdit
				? dataIncumplimiento.activity_status_id || pendienteActivityStatusId
				: pendienteActivityStatusId,
			authority_status_id: isEdit
				? dataIncumplimiento.authority_status_id || pendienteAuthorityStatusId
				: pendienteAuthorityStatusId,
		};

		const resultAction = isEdit
			? await dispatch(
					updateBreachRecord({
						...payload,
						modification_user: actor,
						_id: dataIncumplimiento._id,
					})
			  )
			: await dispatch(
					addNewBreachRecord({
						...payload,
						creation_user: actor,
					})
			  );

		if (resultAction.type.endsWith('/rejected')) return;

		await fetchBreachRecordList(dispatch, inspectionId);
		onDataChange?.();
		setModalAdd(false);
	};

	const handleDeleteSuccess = () => {
		if (inspectionId) fetchBreachRecordList(dispatch, inspectionId);
		onDataChange?.();
	};

	return {
		// UI state
		dataIncumplimiento,
		modalAdd,
		modalDelete,
		isEdit,
		isEnRevision,
		// List data
		incumplimientos,
		isLoadingList,
		// Option arrays for AddEdit form
		inspectionTypeOptions: inspectionTypeListSelect,
		inspectionAuthorityOptions: inspectionAuthorityListSelect,
		responsibleOptions,
		breachResponsibleAreaOptions: breachResponsibleAreaListSelect,
		breachImpactOptions: breachImpactListSelect,
		breachOriginOptions: breachOriginListSelect,
		breachTypeOfEvidenceOptions: breachTypeOfEvidenceListSelect,
		breachCategoryOptions: breachCategoryListSelect,
		breachRiskClassificationOptions: breachRiskClassificationListSelect,
		// Handlers
		handleClickNew,
		handleClickEdit,
		handleClickDelete,
		handleSaveIncumplimiento,
		handleDeleteSuccess,
		setModalAdd,
		setModalDelete,
	};
};

export default useIncumplimientosManagement;
